// Кнопка пульта: shadcn 4.21.1 (base-mira, Base UI), подогнана под ui-spec §4–§5 (задача D4):
// - высоты: обычная `h-8`, тулбар `sm` — `h-7`, чип `xs` — `h-6`, главная кнопка формы `lg` — `h-9`; на телефоне
//   (< 640 px) обычная и главная — `h-10`: касание не меньше 40 px;
// - текст 13 px (`text-base`; в шкале пульта `text-xs` — это 11 px), насыщенность 500;
// - нажатие — класс `.btn` из motion.css: `scale(.97)` за 120 мс; при наведении меняется только цвет, без перехода;
//   переход по всем свойствам и сдвиг на пиксель при нажатии убраны;
// - фокус — общее кольцо `:focus-visible` из index.css (2 px `--ring` с отступом 2 px): отключённая обводка и бледное
//   кольцо (< 3:1) убраны;
// - недоступная кнопка (`disabled`, в том числе с `focusableWhenDisabled`) не нажимается и не сжимается.
import { Button as ButtonPrimitive } from '@base-ui/react/button';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from 'cn';

const buttonVariants = cva(
  "btn inline-flex shrink-0 cursor-pointer items-center justify-center gap-1.5 rounded-md border border-transparent bg-clip-padding text-base font-medium whitespace-nowrap select-none disabled:pointer-events-none disabled:opacity-50 data-disabled:pointer-events-none data-disabled:opacity-50 aria-invalid:border-destructive [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground hover:bg-primary/90',
        outline: 'border-input bg-card text-foreground hover:bg-surface-hover aria-expanded:bg-muted',
        secondary: 'bg-secondary text-secondary-foreground hover:bg-surface-hover aria-expanded:bg-muted',
        ghost: 'text-foreground hover:bg-muted aria-expanded:bg-muted',
        // Опасное действие — тихо: мягкий красный фон, а не заливка (текст --st-error на --st-error-soft ≥ 4,5:1).
        destructive: 'bg-st-error-soft text-st-error hover:bg-st-error-soft/70',
        link: 'text-link underline-offset-4 hover:underline',
      },
      size: {
        default: 'h-8 px-3 max-sm:h-10',
        xs: "h-6 gap-1 px-2 text-sm [&_svg:not([class*='size-'])]:size-3",
        sm: "h-7 gap-1 px-2.5 [&_svg:not([class*='size-'])]:size-3.5",
        lg: 'h-9 px-4 max-sm:h-10',
        icon: 'size-8 max-sm:size-10',
        'icon-xs': "size-6 [&_svg:not([class*='size-'])]:size-3",
        'icon-sm': "size-7 [&_svg:not([class*='size-'])]:size-3.5",
        'icon-lg': 'size-9 max-sm:size-10',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

function Button({
  className,
  variant = 'default',
  size = 'default',
  ...props
}: ButtonPrimitive.Props & VariantProps<typeof buttonVariants>) {
  return <ButtonPrimitive data-slot="button" className={cn(buttonVariants({ variant, size, className }))} {...props} />;
}

export { Button, buttonVariants };
